<template>
	<div class="app-field-numeric app-field" :class="wrapperClass">
		<span v-if="prefix" class="prefix-symbol symbol">{{ prefix }}</span>
		<span v-if="suffix" class="suffix-symbol symbol">{{ suffix }}</span>

		<input
			class="not-read-only:border rounded relative bg-[var(--background-primary)] h-full"
			type="text"
			:value="inputValue"
			:required="required"
			:readonly="isDisabled"
			v-bind="$attrs"
			@input="input"
			@blur="blur"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { useUserStore } from "@/store/useUserStore";
import { asCur, formatNumeric } from "@/helpers/inputHelpers";
import { getInputState } from "@/helpers/getInputState";
import { ValueType } from "@/enums/ValueType";

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(
	defineProps<{
		valueType: ValueType.Number | ValueType.Currency | ValueType.Percentage;
		required?: boolean;
		readOnly?: boolean;
		precision?: number;
		wrapperClass?: string;
		grouping?: boolean;
		prefix?: string;
		suffix?: string;
	}>(),
	{
		required: false,
		readOnly: false,
		precision: 2,
		grouping: true,
	},
);

const prefix = computed(
	() =>
		props.prefix ?? (props.valueType === ValueType.Currency ? "€" : undefined),
);
const suffix = computed(
	() =>
		props.suffix ??
		(props.valueType === ValueType.Percentage ? "%" : undefined),
);

const modifier = computed(() => {
	return props.valueType === ValueType.Percentage ? 100 : 1;
});

const modelValue = defineModel<number>();
const inputValue = ref<string | undefined>(modelToInput(modelValue.value));

const userStore = useUserStore();
const isDisabled = computed(() => {
	return props.readOnly || userStore.user?.role !== "intermediary";
});

function modelToInput(value?: number): string {
	const minimumDecimals = props.grouping
		? value !== undefined && value % 1 === 0
			? 0
			: props.precision
		: 0;

	return (
		formatNumeric(
			value === undefined ? value : value * modifier.value,
			minimumDecimals,
			props.precision,
			props.grouping,
		) ?? ""
	);
}

function inputToModel(value?: string): number | undefined {
	if (value === undefined || value === "") {
		return undefined;
	}

	const valueAsNumber = asCur(value);
	if (valueAsNumber === undefined) {
		return undefined;
	}

	return valueAsNumber / modifier.value;
}

watch(inputValue, (newValue) => {
	modelValue.value = inputToModel(newValue);
});

watch(modelValue, (newValue) => {
	inputValue.value = modelToInput(newValue);
});

function input(event: InputEvent) {
	const target = event.target as HTMLInputElement;

	const { newValue, cursorIndex } = getInputState(
		target.value,
		inputValue.value,
		{
			selectionStart: target.selectionStart ?? Number.NaN,
			maxDecimals: props.precision,
			grouping: props.grouping,
		},
	);

	inputValue.value = newValue;

	void nextTick(() => {
		if (typeof newValue === "string") {
			target.value = newValue;
			target.setSelectionRange(cursorIndex, cursorIndex);
		}
	});
}

function blur(event: FocusEvent) {
	const target = event.target as HTMLInputElement;

	void nextTick(() => {
		inputValue.value = modelToInput(inputToModel(target.value));
		target.value = inputValue.value;
	});
}
</script>

<style lang="scss">
.formkit-input {
	width: 100%;

	input {
		border: none;
	}

	.prefix-symbol {
		left: 0;
	}
}
</style>

<style scoped lang="scss">
.app-field-numeric {
	position: relative;

	.symbol {
		position: absolute;
		z-index: 10;
		top: 50%;
		transform: translateY(-50%);
		text-align: center;
		user-select: none;
		pointer-events: none;
	}

	.prefix-symbol {
		left: 0.75rem;
		display: block;
	}

	.suffix-symbol {
		right: 8px;
		display: block;
	}

	> input {
		text-align: right;
		width: 100%;
		cursor: inherit;
	}

	> input:not(.formkit-input) {
		color: inherit;
		font-family: inherit;

		padding: 0.25rem 0.5rem;

		&[disabled] {
			background-color: transparent;
		}
	}

	> .prefix-symbol ~ input {
		padding-left: 1.5rem;
	}

	> .suffix-symbol ~ input {
		padding-right: 1.5rem;
	}
}
</style>
